import { useState, useRef } from "react";
import { Upload, LayoutGrid, List, Clock } from "lucide-react";
import { EntityType } from "@repo/types/enums";
import type { AddFileAssetsViewModel } from "../../types/viewModels/files/AddFileAssetsViewModel";
import type FileAssetDTO from "../../types/dtos/fileAssets/FileAssetDTO";
import type { RecordId } from "@repo/types/ui/tables";
import {
  useGetFileAssetsByEntityId,
  useAddMultipleFileAssets,
  useDeleteFileAsset,
} from "../../hooks/queries/useFileAssets";
import formatUtcDateTime from "../../helpers/dateTimeHelper";
import { InputSearch } from "../form/input/InputSearch";
import Button from "../ui/button/Button";
import FilesListSkeleton from "./FilesListSkeleton";
import FileModalPreview from "./FileModalPreview";
import FileCardPreview from "./FileCardPreview";
import FileActionsMenu from "./FileActionsMenu";
import UploadProgress from "../ui/uploadprogress/UploadProgress";

export interface FilesListProps {
  recordId: RecordId;
  entityType: EntityType;
}

export type viewType = "grid" | "list";

export default function FilesList({ recordId, entityType }: FilesListProps) {
  const { data: files, isLoading } = useGetFileAssetsByEntityId(
    recordId,
    entityType,
  );
  const { mutateAsync: addMultipleFileAssets, progress: uploadProgress } =
    useAddMultipleFileAssets();
  const { mutateAsync: deleteAsset } = useDeleteFileAsset(recordId, entityType);

  const [view, setView] = useState<viewType>("grid");
  const [search, setSearch] = useState("");
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [totalFiles, setTotalFiles] = useState(0);

  if (!files || isLoading) {
    return <FilesListSkeleton />;
  }

  const filteredFiles = files.filter((file) =>
    file?.OriginalFileName?.toLowerCase().includes(search.toLowerCase()),
  );

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files: FileList | null = e.target.files;
    if (!files) return;

    setTotalFiles(files.length);

    setSearch("");
    setIsUploading(true);

    const fileAssets: AddFileAssetsViewModel = {
      Files: Array.from(files),
      EntityId: recordId,
      EntityType: entityType,
    };

    await addMultipleFileAssets(fileAssets);

    setIsUploading(false);

    e.target.value = "";
  };

  const handleOnPreview = (index: number) => {
    setSelectedIndex(index);
  };

  const handleFileDelete = async (assetId: number) => {
    await deleteAsset(assetId);
    setSelectedIndex(null);
  };

  return (
    <>
      <div className="p-5 space-y-5">
        {/* TOP BAR */}
        <div className="flex flex-col lg:flex-row items-center justify-between gap-3">
          {/* LEFT SIDE */}
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto">
            <Button
              size="sm"
              variant="ghost"
              className="border border-gray-300 h-9"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload size={16} />
              Add file
            </Button>

            {/* SEARCH */}
            <div className="relative w-full sm:w-60">
              <InputSearch
                loading={false}
                onSearch={(e) => setSearch(e)}
                inputClassName="focus:w-full !w-full"
              />
            </div>
          </div>

          {/*  TOGGLE */}
          <div className="flex items-center border border-gray-300 shrink-0">
            <div
              className={`${view == "grid" ? "bg-blue-200 text-black" : ""}`}
            >
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setView("grid")}
                className="h-9"
              >
                <LayoutGrid size={14} />
              </Button>
            </div>

            <div
              className={`${view == "list" ? "bg-blue-200 text-black" : ""}`}
            >
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setView("list")}
                className="h-9"
              >
                <List size={14} />
              </Button>
            </div>
          </div>
        </div>

        <div className="mb-3 text-sm">
          Showing{" "}
          <span className="font-semibold"> {filteredFiles.length} </span>
          out of <span className="font-semibold">
            {files.length || 0}{" "}
          </span>{" "}
          files
        </div>

        {/* CONTENT */}
        {filteredFiles.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <h3 className="text-lg font-semibold text-gray-800">
              No files found
            </h3>

            <p className="text-sm text-gray-500 mt-1 mb-5 max-w-sm">
              We couldn’t find any files matching your search. Try using
              different keywords or upload a new file.
            </p>

            <Button
              size="sm"
              variant="primary"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload size={16} />
              Upload File
            </Button>
          </div>
        ) : view === "grid" ? (
          <div
            className="
            grid gap-4 
            max-h-[calc(100vh-300px)] overflow-y-auto pb-8

            grid-cols-1
            sm:grid-cols-2
            md:grid-cols-3
            lg:grid-cols-4
            xl:grid-cols-5
            2xl:grid-cols-6
          "
          >
            {filteredFiles.map((file: FileAssetDTO, index: number) => (
              <FileCardPreview
                key={file.Id}
                file={file}
                index={index}
                onPreview={handleOnPreview}
                onDelete={handleFileDelete}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-3 max-h-[calc(100vh-300px)] overflow-y-auto pb-8">
            {filteredFiles.map((file: FileAssetDTO, index: number) => (
              <div
                key={file.Id}
                className="flex align-top justify-between p-2 border border-gray-300 rounded hover:bg-gray-50 relative"
              >
                {/* LEFT CLICK AREA */}
                <div
                  className="flex align-top gap-3 flex-1 cursor-pointer"
                  onClick={() => handleOnPreview(index)}
                >
                  {/* IMAGE */}
                  <div className="w-40 h-36 bg-gray-100 rounded overflow-hidden">
                    <FileCardPreview
                      file={file}
                      index={index}
                      actionsEnabled={false}
                      onPreview={handleOnPreview}
                      onDelete={handleFileDelete}
                    />
                  </div>

                  {/* INFO */}
                  <div>
                    <p className="text-sm font-medium">
                      {file.OriginalFileName}
                    </p>

                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Clock size={12} />
                      {formatUtcDateTime(file.CreatedOn)}
                    </div>
                  </div>
                </div>

                <div className="">
                  {/* RIGHT SIDE 3 DOT MENU */}
                  <FileActionsMenu
                    file={file}
                    index={index}
                    onPreview={handleOnPreview}
                    onDelete={handleFileDelete}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        <input
          type="file"
          multiple
          className="hidden"
          ref={fileInputRef}
          onChange={handleFileChange}
        />
      </div>

      {/* MODAL */}
      {selectedIndex !== null && (
        <FileModalPreview
          files={filteredFiles}
          index={selectedIndex}
          onClose={() => setSelectedIndex(null)}
          onDelete={handleFileDelete}
        />
      )}

      {isUploading && (
        <div className="fixed bottom-[30px] left-auto right-[30px]  z-[9999]">
          <UploadProgress progress={uploadProgress} totalFiles={totalFiles} />
        </div>
      )}
    </>
  );
}
